/**
 * Variant Media Grouping
 *
 * Lets a color variant show several images instead of the single featured image
 * Shopify allows per variant. Images are grouped by a "#ColorName" suffix in
 * their alt text (e.g. "Front view #Black"). Images without a "#" tag are shared
 * and stay visible for every color.
 *
 * All product media is rendered into the gallery (see sections/main-product.liquid
 * and sections/featured-product.liquid). This script filters it in place on
 * variant change — it never rebuilds the gallery or destroys the Swiper, so the
 * thumbs position, slider direction and zoom stay intact.
 *
 * Exposes window.updateVariantMediaDisplay(sectionId, variant), called from the
 * variant picker in global.js.
 */
(function () {
  "use strict";

  var HIDDEN_ITEM_CLASS = "product__media-item--variant-hidden";
  var HIDDEN_SUBITEM_CLASS = "product__media-subitem--variant-hidden";

  /**
   * Mirror Shopify's `| handle` filter closely enough for color names:
   * transliterate accents, lowercase, and collapse anything non-alphanumeric
   * into single hyphens. Matching is therefore case-insensitive and handleized
   * on both sides ("Light Blue", "light-blue" and "#light blue" all match).
   */
  function handleize(value) {
    return String(value == null ? "" : value)
      .normalize("NFKD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  // Untagged media (no group) is always visible; a media with a group is shown
  // only when it matches the selected color. With no selected color, show all.
  function isVisibleGroup(mediaGroup, selectedGroup) {
    if (!mediaGroup) return true;
    if (!selectedGroup) return true;
    return mediaGroup === selectedGroup;
  }

  function toggleItems(items, hiddenClass, selectedGroup) {
    for (var i = 0; i < items.length; i++) {
      var el = items[i];
      var group = el.getAttribute("data-media-group") || "";
      var visible = isVisibleGroup(group, selectedGroup);

      el.classList.toggle(hiddenClass, !visible);

      // A hidden node must not be a Swiper slide (display:none + swiper-slide
      // breaks Swiper), so toggle swiper-slide only inside a .swiper-wrapper.
      var parent = el.parentElement;
      if (parent && parent.classList.contains("swiper-wrapper")) {
        if (visible) {
          el.classList.add("swiper-slide");
        } else {
          el.classList.remove("swiper-slide");
        }
      }
    }
  }

  // Recompute the slider after slides were toggled, keeping the current
  // direction. Never destroy()/re-create — that resets thumbs layout and heights.
  function refreshSwiper(swiper) {
    if (!swiper || swiper.destroyed) return;

    var wasVertical =
      typeof swiper.isVertical === "function"
        ? swiper.isVertical()
        : swiper.params.direction === "vertical";

    try {
      swiper.updateSize();
      swiper.updateSlides();
      swiper.updateProgress();
      swiper.updateSlidesClasses();
      swiper.update();
    } catch (e) {
      return;
    }

    var isVerticalNow =
      typeof swiper.isVertical === "function"
        ? swiper.isVertical()
        : swiper.params.direction === "vertical";

    // Restore the live direction if update() flipped it (e.g. horizontal thumbs
    // under the image must not become a vertical side column after filtering).
    if (
      wasVertical !== isVerticalNow &&
      typeof swiper.changeDirection === "function"
    ) {
      swiper.changeDirection(wasVertical ? "vertical" : "horizontal", false);
    }
  }

  // Slide to the featured media of the selected variant when it is visible,
  // otherwise to the first visible slide. Hidden slides are skipped.
  function slideToTarget(swiper, featuredMediaId, hiddenClass) {
    if (!swiper || swiper.destroyed) return;

    var slides = swiper.slides || [];
    var targetIndex = -1;

    if (featuredMediaId) {
      for (var i = 0; i < slides.length; i++) {
        if (
          slides[i].getAttribute &&
          slides[i].getAttribute("data-media-id") === featuredMediaId &&
          !slides[i].classList.contains(hiddenClass)
        ) {
          targetIndex = i;
          break;
        }
      }
    }

    if (targetIndex === -1) {
      for (var j = 0; j < slides.length; j++) {
        if (!slides[j].classList.contains(hiddenClass)) {
          targetIndex = j;
          break;
        }
      }
    }

    if (targetIndex >= 0) {
      try {
        swiper.slideTo(targetIndex, 0);
      } catch (e) {
        /* noop */
      }
    }
  }

  function scrollStackedIntoView(root, featuredMediaId) {
    if (window.innerWidth < 990) return;

    var target = null;
    if (featuredMediaId) {
      target = root.querySelector(
        '.product__media-item[data-media-id="' +
          featuredMediaId +
          '"]:not(.' +
          HIDDEN_ITEM_CLASS +
          ")"
      );
    }
    if (!target) {
      target = root.querySelector(
        ".product__media-item:not(." + HIDDEN_ITEM_CLASS + ")"
      );
    }
    if (!target) return;

    var top = target.getBoundingClientRect().top + window.scrollY;
    try {
      window.scrollTo({ top: top - 100, behavior: "smooth" });
    } catch (e) {
      window.scrollTo(0, top - 100);
    }
  }

  window.updateVariantMediaDisplay = function (sectionId, variant, options) {
    options = options || {};
    var root = document.querySelector(
      '[data-section="product-media-' +
        sectionId +
        '"][data-alt-media-grouping="true"]'
    );
    if (!root) return;

    var position = parseInt(
      root.getAttribute("data-group-option-position") || "0",
      10
    );

    var selectedGroup = "";
    if (position >= 1 && variant && Array.isArray(variant.options)) {
      selectedGroup = handleize(variant.options[position - 1]);
    }

    // 1) Toggle visibility of main slides and thumbnails.
    toggleItems(
      root.querySelectorAll(".product__media-item[data-media-id]"),
      HIDDEN_ITEM_CLASS,
      selectedGroup
    );
    toggleItems(
      root.querySelectorAll(".product__media-subitem[data-media-sub-id]"),
      HIDDEN_SUBITEM_CLASS,
      selectedGroup
    );

    // 2) Toggle the zoom modal slides (a separate DOM node).
    var zoomSlider = document.querySelector(
      '[data-section-id="' + sectionId + '"].js-popup-slider'
    );
    if (zoomSlider) {
      toggleItems(
        zoomSlider.querySelectorAll(
          ".product-media-modal__item[data-media-modal-id]"
        ),
        HIDDEN_ITEM_CLASS,
        selectedGroup
      );
    }

    // 3) Refresh the sliders in place.
    var mainSlider = root.querySelector(".js-media-list");
    var thumbsSlider = root.querySelector(".js-media-sublist");
    var mainSwiper = mainSlider && mainSlider.swiper;
    var thumbsSwiper = thumbsSlider && thumbsSlider.swiper;

    refreshSwiper(mainSwiper);
    refreshSwiper(thumbsSwiper);
    if (zoomSlider && zoomSlider.swiper) refreshSwiper(zoomSlider.swiper);

    // Keep the thumbs/main link in sync after the slide set changed.
    if (mainSwiper && mainSwiper.thumbs && typeof mainSwiper.thumbs.update === "function") {
      try {
        mainSwiper.thumbs.update(true);
      } catch (e) {
        /* noop */
      }
    }

    // 4) Move to the featured media of the selected variant (or first visible).
    var featuredMediaId =
      variant && variant.featured_media
        ? sectionId + "-" + variant.featured_media.id
        : null;
    if (mainSwiper) {
      slideToTarget(mainSwiper, featuredMediaId, HIDDEN_ITEM_CLASS);
    } else if (!options.suppressScroll) {
      scrollStackedIntoView(root, featuredMediaId);
    }

    // 5) Hide the thumbs strip when a single (or no) thumb is visible.
    var thumbsWrapper = root.querySelector(".product__media-sublist-wrapper");
    if (thumbsWrapper) {
      var visibleThumbs = root.querySelectorAll(
        ".product__media-subitem:not(." + HIDDEN_SUBITEM_CLASS + ")"
      );
      // Inline display loses to the theme's `display:none !important` mobile
      // rule, so the thumbs stay hidden on mobile regardless.
      thumbsWrapper.style.display = visibleThumbs.length <= 1 ? "none" : "";
    }
  };

  function getSelectedVariant(sectionId) {
    var picker = document.querySelector(
      'variant-selects[data-section="' +
        sectionId +
        '"], variant-radios[data-section="' +
        sectionId +
        '"]'
    );
    if (!picker) return null;

    var json = picker.querySelector("[data-selected-variant]");
    if (!json) return null;

    try {
      return JSON.parse(json.textContent);
    } catch (e) {
      return null;
    }
  }

  function initRoot(root) {
    if (!root || root.getAttribute("data-alt-media-grouping") !== "true") return;

    var sectionId = (root.getAttribute("data-section") || "").replace(
      "product-media-",
      ""
    );
    if (!sectionId) return;

    // First paint is already filtered by Liquid; this reconciles the sliders
    // and lands on the featured media of the (possibly deep-linked) variant.
    var run = function () {
      window.updateVariantMediaDisplay(sectionId, getSelectedVariant(sectionId), {
        suppressScroll: true,
      });
    };

    var mainList = root.querySelector(".js-media-list");
    if (mainList && !mainList.swiper) {
      var tries = 0;
      var timer = setInterval(function () {
        tries++;
        if (mainList.swiper || tries > 60) {
          clearInterval(timer);
          run();
        }
      }, 16);
    } else {
      run();
    }
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll(
      '[data-alt-media-grouping="true"]'
    );
    for (var i = 0; i < roots.length; i++) initRoot(roots[i]);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener("shopify:section:load", function (event) {
    initAll(event.target);
  });
})();
